import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import CompaniesForm from 'src/shared/OrgsSearch';
import { SuppliersList } from './SuppliersList';
import { isEqual, xor } from 'lodash';
import SupplierSearchLogicQuickFilter from './SupplierSearchLogicQuickFilter';
import { AwardStatus } from '@tendium/prom-types';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { SupplierOrg, SupplierString } from 'src/models/procurements/Tenders/types';
import { GenericSearchDropdown, IGenericSearchDropdownProps } from 'src/shared';
import { useTranslation } from 'react-i18next';

export const SupplierQuickFilter: FC<IGenericSearchDropdownProps> = props => {
  const { t } = useTranslation();

  const [{ vars, initVars }, updateSearchArgs] = useApiTenders();

  const [lastAddedOrgIds, setLastAddedOrgIds] = useState<string[]>([]);

  const searchArgsSupplierOrgIds: SupplierOrg[] = useMemo(() => {
    return (vars?.supplierOrgIds || []).map(supplierOrg => ({
      orgId: supplierOrg.orgId,
      status: supplierOrg.status
    }));
  }, [vars?.supplierOrgIds]);

  const searchArgsSupplierSearchStrings: SupplierString[] = useMemo(() => {
    return (vars?.supplierSearchStrings || []).map(supplierString => ({
      orgName: supplierString.orgName,
      status: supplierString.status
    }));
  }, [vars?.supplierSearchStrings]);

  const supplierIds = useMemo(() => {
    return (searchArgsSupplierOrgIds || []).map(supplierOrg => supplierOrg.orgId);
  }, [searchArgsSupplierOrgIds]);

  const supplierSearchStrings = useMemo(() => {
    return (searchArgsSupplierSearchStrings || []).map(supplierSearchString => supplierSearchString.orgName);
  }, [searchArgsSupplierSearchStrings]);

  const onAddOrgs = useCallback(
    (exact?: string[], prefix?: string[]) => {
      const prefixes = prefix?.filter(p => !supplierSearchStrings.includes(p));

      if (xor(exact ?? [], supplierIds).length !== 0 || prefixes?.length !== 0) {
        const uniqueCompaniesIds: SupplierOrg[] = [
          ...(exact?.map(exact => ({ orgId: exact, status: AwardStatus.All })) ?? []),
          ...searchArgsSupplierOrgIds
        ];
        const uniqueSearchStrings: SupplierString[] = [
          ...(prefixes?.map(prefix => ({ orgName: prefix, status: AwardStatus.All })) ?? []),
          ...searchArgsSupplierSearchStrings
        ];
        exact && setLastAddedOrgIds(() => exact);
        updateSearchArgs({ supplierOrgIds: uniqueCompaniesIds, supplierSearchStrings: uniqueSearchStrings });
      }
    },
    [searchArgsSupplierOrgIds, searchArgsSupplierSearchStrings, supplierIds, supplierSearchStrings, updateSearchArgs]
  );

  const onRemoveOrgs = useCallback(
    (updatingIds, updatingSearchStrings) =>
      updateSearchArgs({ supplierOrgIds: updatingIds, supplierSearchStrings: updatingSearchStrings }),
    [updateSearchArgs]
  );

  const isChanged = useMemo(
    () =>
      !isEqual(vars?.supplierOrgIds, initVars.supplierOrgIds) ||
      !isEqual(vars?.supplierSearchStrings, initVars.supplierSearchStrings) ||
      vars?.supplierSearchLogic !== initVars.supplierSearchLogic,
    [
      initVars.supplierOrgIds,
      initVars.supplierSearchLogic,
      initVars.supplierSearchStrings,
      vars?.supplierOrgIds,
      vars?.supplierSearchLogic,
      vars?.supplierSearchStrings
    ]
  );

  return (
    <GenericSearchDropdown
      {...props}
      label={t('ExpiringContracts.suppliers')}
      overlay={
        <div className={styles.companiesFilter}>
          <CompaniesForm exact={supplierIds} prefix={supplierSearchStrings} mode={'multiple'} onSelect={onAddOrgs} />
          <SuppliersList
            supplierOrgIds={searchArgsSupplierOrgIds}
            supplierSearchStrings={searchArgsSupplierSearchStrings}
            lastAddedOrgIds={lastAddedOrgIds}
            removeOrgs={onRemoveOrgs}
          />
          <SupplierSearchLogicQuickFilter />
        </div>
      }
      overlayClassName={styles.overlay}
      isChanged={isChanged}
      special={props.special ? props.special : !vars.profileId && isChanged}
    />
  );
};

export default SupplierQuickFilter;
